import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBox, 
  faListUl, 
  faUsers, 
  faChartLine, 
  faShoppingBag,
  faSignOutAlt,
  faTachometerAlt,
  faUserCircle,
  faTags,
  faCalendarTimes,
  faMapMarkerAlt,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import ProductsSection from './ProductsSection';
import CategoriesSection from './CategoriesSection';
import CouponsSection from './CouponsSection';
import StaffSection from '../dashboard/StaffSection';
import SalesSection from './SalesSection';
import OrdersSection from './OrdersSection';
import DashboardOverview from './DashboardOverview';
import CalendarBlockingSection from './CalendarBlockingSection';
import LocationsSection from './LocationsSection';
import '../../styles/admin-dashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  // Removed automatic role initialization - roles should be created manually by admins
  // This prevents permission conflicts during dashboard load
  
  // Set active tab from URL hash if present
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['overview', 'products', 'categories', 'coupons', 'staff', 'sales', 'orders', 'locations', 'calendar-blocking', 'roles'].includes(hash)) {
      setActiveTab(hash);
    }
  }, []);
  
  // Update URL hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // Close the mobile nav drawer whenever a tab is selected
  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeTab]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileNavOpen]);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  // Render the active section based on the selected tab
  const renderActiveSection = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview />;
      case 'products':
        return <ProductsSection />;
      case 'categories':
        return <CategoriesSection />;
      case 'coupons':
        return <CouponsSection />;
      case 'staff':
        return <StaffSection />;
      case 'sales':
        return <SalesSection />;
      case 'orders':
        return <OrdersSection />;
      case 'locations':
        return <LocationsSection />;
      case 'calendar-blocking':
        return <CalendarBlockingSection />;
      default:
        return <DashboardOverview />;
    }
  };
  
  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <div className="admin-header-left">
          <button
            className="admin-hamburger"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
          <h1>Admin Dashboard</h1>
        </div>
        <div className="user-info-header">
          <span>Welcome, {currentUser?.displayName || 'User'}</span>
          <button className="logout-button" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} /> Logout
          </button>
        </div>
      </div>

      <div className="admin-dashboard-content">
        {mobileNavOpen && (
          <div
            className="admin-nav-backdrop"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
        )}
        <div className={`admin-dashboard-sidebar ${mobileNavOpen ? 'mobile-open' : ''}`}>
          <button
            className="admin-drawer-close"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className="admin-user-info">
            <div className="admin-user-avatar">
              <FontAwesomeIcon icon={faUserCircle} />
            </div>
            <div className="admin-user-details">
              <h3>{currentUser?.displayName || 'User'}</h3>
              <p>{currentUser?.email || ''}</p>
              <span className="admin-badge">{currentUser?.roleName || currentUser?.role || 'Staff'}</span>
            </div>
          </div>
          
          <nav className="admin-dashboard-nav">
            <ul>
              <li 
                className={activeTab === 'overview' ? 'active' : ''}
                onClick={() => setActiveTab('overview')}
              >
                <FontAwesomeIcon icon={faTachometerAlt} />
                <span>Overview</span>
              </li>
              
              <li 
                className={activeTab === 'products' ? 'active' : ''}
                onClick={() => setActiveTab('products')}
              >
                <FontAwesomeIcon icon={faBox} />
                <span>Products</span>
              </li>
              
              <li 
                className={activeTab === 'categories' ? 'active' : ''}
                onClick={() => setActiveTab('categories')}
              >
                <FontAwesomeIcon icon={faListUl} />
                <span>Categories</span>
              </li>
              
              <li 
                className={activeTab === 'coupons' ? 'active' : ''}
                onClick={() => setActiveTab('coupons')}
              >
                <FontAwesomeIcon icon={faTags} />
                <span>Coupons</span>
              </li>
              
              <li 
                className={activeTab === 'staff' ? 'active' : ''}
                onClick={() => setActiveTab('staff')}
              >
                <FontAwesomeIcon icon={faUsers} />
                <span>Staff</span>
              </li>
              
              <li 
                className={activeTab === 'sales' ? 'active' : ''}
                onClick={() => setActiveTab('sales')}
              >
                <FontAwesomeIcon icon={faChartLine} />
                <span>Sales</span>
              </li>
              
              <li 
                className={activeTab === 'orders' ? 'active' : ''}
                onClick={() => setActiveTab('orders')}
              >
                <FontAwesomeIcon icon={faShoppingBag} />
                <span>Orders</span>
              </li>
              
              <li 
                className={activeTab === 'locations' ? 'active' : ''}
                onClick={() => setActiveTab('locations')}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>Locations</span>
              </li>
              
              <li 
                className={activeTab === 'calendar-blocking' ? 'active' : ''}
                onClick={() => setActiveTab('calendar-blocking')}
              >
                <FontAwesomeIcon icon={faCalendarTimes} />
                <span>Calendar Blocking</span>
              </li>
              
              <li className="logout-item" onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>Logout</span>
              </li>
            </ul>
          </nav>
        </div>
        
        <div className="admin-dashboard-main">
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
